<script lang="ts" setup>
import { reactive, computed, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { AccountDetails, AccountFacets, AccountListFilter, SyncAccountStripeResult } from '@/types'
import { useAxios } from '@/composables/useAxios'
import { useAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid, useImpersonation } from '@/composables'
import { STATUS_CODES } from '@/constants'

const { t } = useI18n()
const router = useRouter()

const props = defineProps<{
  users: AccountDetails[]
  loading?: boolean
  total?: number
  page?: number
  itemsPerPage?: number
  totalPages?: number
  search?: string
  facets?: AccountFacets | null
}>()

const emits = defineEmits<{
  (e: 'refresh'): void
  (e: 'update:page', page: number): void
  (e: 'update:itemsPerPage', value: number): void
  (e: 'update:filter', value: AccountListFilter): void
}>()

const notifyStore = useNotifyStore()
const { handleAxiosError } = useAxiosError()
const { startImpersonation } = useImpersonation()

const data = reactive({
  togglingUser: null as number | null,
  activeFilter: 'all' as AccountListFilter,
  syncStripeDialog: {
    show: false,
    user: null as AccountDetails | null,
    loading: false
  },
  impersonateDialog: {
    show: false,
    user: null as AccountDetails | null,
    loading: false
  }
})

// Only ever used for the empty state's wording; the rows themselves arrive
// already searched and filtered, because a page-local filter can only see the
// page and the list is ordered newest first.
const searchValue = toRef(() => props.search ?? '')

// Toggle filter - clicking same filter again clears it
const toggleFilter = (filter: AccountListFilter) => {
  data.activeFilter = data.activeFilter === filter ? 'all' : filter
  emits('update:filter', data.activeFilter)
}

// Counted by the server over the whole search result. Counting the page here
// instead is what made the pills claim zero admins on an account that has one:
// the only admin sat on page two.
const stats = computed(() => ({
  active: props.facets?.active ?? 0,
  inactive: props.facets?.inactive ?? 0,
  unverified: props.facets?.unverified ?? 0,
  admins: props.facets?.by_role.admin ?? 0,
  resellers: props.facets?.by_role.reseller ?? 0,
  customers: props.facets?.by_role.customer ?? 0
}))

// Pagination helpers
const hasPagination = computed(() => props.total !== undefined && props.page !== undefined)
const showingFrom = computed(() => hasPagination.value ? ((props.page! - 1) * (props.itemsPerPage || 50)) + 1 : 1)
const showingTo = computed(() => hasPagination.value ? Math.min(showingFrom.value + props.users.length - 1, props.total!) : props.users.length)

const getDisplayName = (user: AccountDetails): string => {
  if (user.account_type === 'organization' && user.org) {
    return user.org
  }
  const parts = [user.name, user.surname].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : '-'
}

const getRoleColor = (role?: string): string => {
  switch (role) {
    case 'admin':
      return 'error'
    case 'reseller':
      return 'warning'
    case 'customer':
      return 'info'
    default:
      return 'default'
  }
}

const getTypeIcon = (type?: string): string => {
  return type === 'organization' ? 'mdi-domain' : 'mdi-account'
}

// Toggle active status
const toggleActiveStatus = (user: AccountDetails, event: Event) => {
  event.stopPropagation()
  if (data.togglingUser || !user.id) return

  data.togglingUser = user.id
  const newValue = !user.active

  useAxios<AccountDetails, null>('POST', `/account/${user.id}`, true, {
    ...user,
    active: newValue
  })
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        notifyStore.createNotify({
          id: uuid(),
          title: t('common.success'),
          message: newValue ? t('dashboard.admin.users.notifications.activated', { name: getDisplayName(user) }) : t('dashboard.admin.users.notifications.deactivated', { name: getDisplayName(user) }),
          alertType: 'success'
        })
        emits('refresh')
      }
    })
    .catch((error) => {
      handleAxiosError(error)
    })
    .finally(() => {
      data.togglingUser = null
    })
}

const handleRowClick = (user: AccountDetails) => {
  router.push({ name: 'dashboardAdminUserDetail', params: { id: user.id } })
}

const openSyncStripeDialog = (user: AccountDetails, event: Event) => {
  event.stopPropagation()
  data.syncStripeDialog.user = user
  data.syncStripeDialog.show = true
}

const closeSyncStripeDialog = () => {
  data.syncStripeDialog.show = false
  data.syncStripeDialog.user = null
}

const confirmSyncStripe = async () => {
  const user = data.syncStripeDialog.user
  if (!user?.id) return

  data.syncStripeDialog.loading = true

  try {
    const res = await useAxios<null, SyncAccountStripeResult>(
      'POST',
      `/admin/account/${user.id}/sync-stripe`,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      const result = res.data
      notifyStore.createNotify({
        id: uuid(),
        title: t('common.success'),
        message: t('dashboard.admin.users.syncStripe.successDetail', {
          methods: result.payment_method_synced ? 1 : 0,
          subs: result.subscriptions_created
        }),
        alertType: 'success'
      })
      closeSyncStripeDialog()
      emits('refresh')
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'UserTable:syncStripe' })
    notifyStore.createNotify({
      id: uuid(),
      title: t('common.error'),
      message: getErrorMessage(error as Error),
      alertType: 'error'
    })
  } finally {
    data.syncStripeDialog.loading = false
  }
}

const openImpersonateDialog = (user: AccountDetails, event: Event) => {
  event.stopPropagation()
  data.impersonateDialog.user = user
  data.impersonateDialog.show = true
}

const closeImpersonateDialog = () => {
  data.impersonateDialog.show = false
  data.impersonateDialog.user = null
}

const confirmImpersonate = async () => {
  const user = data.impersonateDialog.user
  if (!user?.id) return

  data.impersonateDialog.loading = true

  try {
    await startImpersonation(user.id)
    closeImpersonateDialog()
  } finally {
    data.impersonateDialog.loading = false
  }
}
</script>

<template>
  <div class="user-table-container">
    <!-- Stats Pills (clickable filters) -->
    <div class="stats-pills">
      <!-- Status filters -->
      <button
        class="stat-pill"
        :class="{ active: data.activeFilter === 'active' }"
        @click="toggleFilter('active')"
      >
        <v-icon icon="mdi-account-check" size="16" />
        <span>{{ $t('dashboard.admin.users.filters.active', { count: stats.active }) }}</span>
        <v-icon v-if="data.activeFilter === 'active'" icon="mdi-close-circle" size="14" class="clear-icon" />
      </button>
      <button
        class="stat-pill inactive"
        :class="{ active: data.activeFilter === 'inactive' }"
        @click="toggleFilter('inactive')"
      >
        <v-icon icon="mdi-account-off" size="16" />
        <span>{{ $t('dashboard.admin.users.filters.inactive', { count: stats.inactive }) }}</span>
        <v-icon v-if="data.activeFilter === 'inactive'" icon="mdi-close-circle" size="14" class="clear-icon" />
      </button>
      <button
        class="stat-pill unverified"
        :class="{ active: data.activeFilter === 'unverified' }"
        @click="toggleFilter('unverified')"
      >
        <v-icon icon="mdi-email-alert" size="16" />
        <span>{{ $t('dashboard.admin.users.filters.unverified', { count: stats.unverified }) }}</span>
        <v-icon v-if="data.activeFilter === 'unverified'" icon="mdi-close-circle" size="14" class="clear-icon" />
      </button>

      <div class="pill-divider"></div>

      <!-- Role filters -->
      <button
        class="stat-pill admin"
        :class="{ active: data.activeFilter === 'admin' }"
        @click="toggleFilter('admin')"
      >
        <v-icon icon="mdi-shield-account" size="16" />
        <span>{{ $t('dashboard.admin.users.filters.admins', { count: stats.admins }) }}</span>
        <v-icon v-if="data.activeFilter === 'admin'" icon="mdi-close-circle" size="14" class="clear-icon" />
      </button>
      <button
        class="stat-pill reseller"
        :class="{ active: data.activeFilter === 'reseller' }"
        @click="toggleFilter('reseller')"
      >
        <v-icon icon="mdi-account-tie" size="16" />
        <span>{{ $t('dashboard.admin.users.filters.reseller', { count: stats.resellers }) }}</span>
        <v-icon v-if="data.activeFilter === 'reseller'" icon="mdi-close-circle" size="14" class="clear-icon" />
      </button>
      <button
        class="stat-pill customer"
        :class="{ active: data.activeFilter === 'customer' }"
        @click="toggleFilter('customer')"
      >
        <v-icon icon="mdi-account" size="16" />
        <span>{{ $t('dashboard.admin.users.filters.customers', { count: stats.customers }) }}</span>
        <v-icon v-if="data.activeFilter === 'customer'" icon="mdi-close-circle" size="14" class="clear-icon" />
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular color="cyan" indeterminate size="40" width="3" />
      <span class="loading-text">{{ $t('dashboard.admin.users.loading') }}</span>
    </div>

    <!-- Table -->
    <div v-else class="glass-table">
      <div class="table-head">
        <div class="head-cell cell-name">{{ $t('dashboard.admin.users.table.name') }}</div>
        <div class="head-cell cell-email">{{ $t('dashboard.admin.users.table.email') }}</div>
        <div class="head-cell cell-type">{{ $t('dashboard.admin.users.table.type') }}</div>
        <div class="head-cell cell-role">{{ $t('dashboard.admin.users.table.role') }}</div>
        <div class="head-cell cell-status">{{ $t('dashboard.admin.users.table.status') }}</div>
        <div class="head-cell cell-actions">{{ $t('dashboard.admin.users.table.actions') }}</div>
      </div>

      <div v-if="users.length === 0" class="empty-state">
        <v-icon icon="mdi-account-search" size="48" />
        <span v-if="searchValue && data.activeFilter !== 'all'">
          {{ $t('dashboard.admin.users.empty.noResultsFilter', {
            filter: data.activeFilter === 'active' ? $t('dashboard.admin.users.empty.activeUsers') :
                   data.activeFilter === 'inactive' ? $t('dashboard.admin.users.empty.inactiveUsers') :
                   data.activeFilter === 'unverified' ? $t('dashboard.admin.users.empty.unverifiedUsers') :
                   data.activeFilter === 'admin' ? $t('dashboard.admin.users.empty.adminUsers') :
                   data.activeFilter === 'reseller' ? $t('dashboard.admin.users.empty.resellerUsers') : $t('dashboard.admin.users.empty.customerUsers'),
            query: searchValue
          }) }}
        </span>
        <span v-else-if="searchValue">{{ $t('dashboard.admin.users.empty.noResults', { query: searchValue }) }}</span>
        <span v-else-if="data.activeFilter !== 'all'">
          {{ $t('dashboard.admin.users.empty.noFilterResults', {
            filter: data.activeFilter === 'active' ? $t('dashboard.admin.users.empty.activeFilter') :
                   data.activeFilter === 'inactive' ? $t('dashboard.admin.users.empty.inactiveFilter') :
                   data.activeFilter === 'unverified' ? $t('dashboard.admin.users.empty.unverifiedFilter') :
                   data.activeFilter === 'admin' ? $t('dashboard.admin.users.empty.adminsFilter') :
                   data.activeFilter === 'reseller' ? $t('dashboard.admin.users.empty.resellersFilter') : $t('dashboard.admin.users.empty.customersFilter')
          }) }}
        </span>
        <span v-else>{{ $t('dashboard.admin.users.empty.noUsers') }}</span>
      </div>

      <div
        v-for="user in users"
        :key="user.id"
        class="table-row"
        @click="handleRowClick(user)"
      >
        <div class="row-cell cell-name">
          <div class="user-info">
            <div class="user-avatar" :class="user.account_type">
              <v-icon :icon="getTypeIcon(user.account_type)" size="18" />
            </div>
            <div class="user-details">
              <span class="user-name">{{ getDisplayName(user) }}</span>
              <span v-if="user.account_type === 'organization' && user.name" class="user-contact">
                {{ user.name }} {{ user.surname }}
              </span>
            </div>
          </div>
        </div>

        <div class="row-cell cell-email">
          <span class="email-text">{{ user.email }}</span>
          <v-icon
            v-if="user.email_verified"
            icon="mdi-check-decagram"
            size="14"
            class="verified-icon"
            :title="$t('dashboard.admin.users.emailVerified')"
          />
        </div>

        <div class="row-cell cell-type">
          <span class="type-badge" :class="user.account_type">
            {{ user.account_type === 'organization' ? $t('dashboard.admin.users.types.organization') : $t('dashboard.admin.users.types.person') }}
          </span>
        </div>

        <div class="row-cell cell-role">
          <v-chip
            :color="getRoleColor(user.role)"
            size="small"
            variant="tonal"
            label
          >
            {{ user.role }}
          </v-chip>
        </div>

        <div class="row-cell cell-status">
          <button
            class="status-toggle"
            :class="user.active ? 'status-active' : 'status-inactive'"
            :disabled="data.togglingUser === user.id"
            :title="$t('dashboard.admin.users.table.toggleStatus')"
            @click="toggleActiveStatus(user, $event)"
          >
            <v-progress-circular
              v-if="data.togglingUser === user.id"
              indeterminate
              size="14"
              width="2"
            />
            <v-icon v-else :icon="user.active ? 'mdi-check' : 'mdi-close'" size="16" />
          </button>
        </div>

        <div class="row-cell cell-actions">
          <button
            v-if="user.role !== 'admin'"
            class="impersonate-btn"
            :title="$t('dashboard.admin.users.impersonate.button')"
            @click="openImpersonateDialog(user, $event)"
          >
            <v-icon icon="mdi-account-switch" size="18" />
          </button>
          <button
            class="sync-stripe-btn"
            :title="$t('dashboard.admin.users.syncStripe.button')"
            @click="openSyncStripeDialog(user, $event)"
          >
            <v-icon icon="mdi-credit-card-sync" size="18" />
          </button>
          <button class="edit-btn" :title="$t('dashboard.admin.users.table.edit')" @click.stop="handleRowClick(user)">
            <v-icon icon="mdi-pencil" size="18" />
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination Controls -->
    <div v-if="hasPagination && !loading" class="pagination-controls">
      <div class="pagination-info">
        {{ $t('dashboard.admin.users.pagination.showing', { from: showingFrom, to: showingTo, total: props.total }) }}
      </div>
      <div class="pagination-buttons">
        <button
          class="pagination-btn"
          :disabled="page === 1"
          @click="emits('update:page', 1)"
        >
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="page === 1"
          @click="emits('update:page', page! - 1)"
        >
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">
          {{ page }} / {{ totalPages }}
        </span>
        <button
          class="pagination-btn"
          :disabled="page === totalPages"
          @click="emits('update:page', page! + 1)"
        >
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="page === totalPages"
          @click="emits('update:page', totalPages!)"
        >
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>

    <!-- Sync Stripe Confirmation Dialog -->
    <v-dialog v-model="data.syncStripeDialog.show" max-width="420" persistent>
      <div class="sync-stripe-dialog">
        <div class="dialog-header">
          <v-icon icon="mdi-credit-card-sync" size="24" class="dialog-icon" />
          <h3>{{ $t('dashboard.admin.users.syncStripe.dialogTitle') }}</h3>
        </div>
        <div class="dialog-content">
          <p class="dialog-message">{{ $t('dashboard.admin.users.syncStripe.dialogMessage') }}</p>
          <div v-if="data.syncStripeDialog.user" class="user-info-box">
            <div class="info-row">
              <span class="info-label">{{ $t('dashboard.admin.users.table.email') }}:</span>
              <span class="info-value">{{ data.syncStripeDialog.user.email }}</span>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <button
            class="dialog-btn cancel-btn"
            :disabled="data.syncStripeDialog.loading"
            @click="closeSyncStripeDialog"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            class="dialog-btn confirm-btn"
            :disabled="data.syncStripeDialog.loading"
            @click="confirmSyncStripe"
          >
            <v-progress-circular
              v-if="data.syncStripeDialog.loading"
              indeterminate
              size="16"
              width="2"
            />
            <template v-else>
              <v-icon icon="mdi-sync" size="16" />
              <span>{{ $t('common.confirm') }}</span>
            </template>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Impersonate Confirmation Dialog -->
    <v-dialog v-model="data.impersonateDialog.show" max-width="420" persistent>
      <div class="impersonate-dialog">
        <div class="dialog-header">
          <v-icon icon="mdi-account-switch" size="24" class="dialog-icon impersonate" />
          <h3>{{ $t('dashboard.admin.users.impersonate.dialogTitle') }}</h3>
        </div>
        <div class="dialog-content">
          <p class="dialog-message">{{ $t('dashboard.admin.users.impersonate.dialogMessage') }}</p>
          <div v-if="data.impersonateDialog.user" class="user-info-box">
            <div class="info-row">
              <span class="info-label">{{ $t('dashboard.admin.users.table.name') }}:</span>
              <span class="info-value">{{ getDisplayName(data.impersonateDialog.user) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('dashboard.admin.users.table.email') }}:</span>
              <span class="info-value">{{ data.impersonateDialog.user.email }}</span>
            </div>
          </div>
          <p class="dialog-warning">{{ $t('dashboard.admin.users.impersonate.warning') }}</p>
        </div>
        <div class="dialog-actions">
          <button
            class="dialog-btn cancel-btn"
            :disabled="data.impersonateDialog.loading"
            @click="closeImpersonateDialog"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            class="dialog-btn confirm-btn impersonate"
            :disabled="data.impersonateDialog.loading"
            @click="confirmImpersonate"
          >
            <v-progress-circular
              v-if="data.impersonateDialog.loading"
              indeterminate
              size="16"
              width="2"
            />
            <template v-else>
              <v-icon icon="mdi-account-switch" size="16" />
              <span>{{ $t('dashboard.admin.users.impersonate.confirm') }}</span>
            </template>
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
.user-table-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stats-pills {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.pill-divider {
  width: 1px;
  height: 24px;
  background: rgba(120, 163, 255, 0.2);
  margin: 0 4px;
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 20px;
  font-size: 0.8rem;
  color: #4CAF50;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2);
  }

  &.active {
    background: rgba(76, 175, 80, 0.25);
    border-color: rgba(76, 175, 80, 0.5);
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.15);
  }

  .clear-icon {
    margin-left: 2px;
    opacity: 0.7;
  }

  &.inactive {
    background: rgba(158, 158, 158, 0.1);
    border-color: rgba(158, 158, 158, 0.2);
    color: #9E9E9E;

    &:hover {
      box-shadow: 0 4px 12px rgba(158, 158, 158, 0.2);
    }

    &.active {
      background: rgba(158, 158, 158, 0.25);
      border-color: rgba(158, 158, 158, 0.5);
      box-shadow: 0 0 0 2px rgba(158, 158, 158, 0.15);
    }
  }

  &.unverified {
    background: rgba(255, 193, 7, 0.1);
    border-color: rgba(255, 193, 7, 0.2);
    color: #FFC107;

    &:hover {
      box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2);
    }

    &.active {
      background: rgba(255, 193, 7, 0.25);
      border-color: rgba(255, 193, 7, 0.5);
      box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.15);
    }
  }

  &.admin {
    background: rgba(244, 67, 54, 0.1);
    border-color: rgba(244, 67, 54, 0.2);
    color: #F44336;

    &:hover {
      box-shadow: 0 4px 12px rgba(244, 67, 54, 0.2);
    }

    &.active {
      background: rgba(244, 67, 54, 0.25);
      border-color: rgba(244, 67, 54, 0.5);
      box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.15);
    }
  }

  &.reseller {
    background: rgba(255, 152, 0, 0.1);
    border-color: rgba(255, 152, 0, 0.2);
    color: #FF9800;

    &:hover {
      box-shadow: 0 4px 12px rgba(255, 152, 0, 0.2);
    }

    &.active {
      background: rgba(255, 152, 0, 0.25);
      border-color: rgba(255, 152, 0, 0.5);
      box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.15);
    }
  }

  &.customer {
    background: rgba(33, 150, 243, 0.1);
    border-color: rgba(33, 150, 243, 0.2);
    color: #2196F3;

    &:hover {
      box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
    }

    &.active {
      background: rgba(33, 150, 243, 0.25);
      border-color: rgba(33, 150, 243, 0.5);
      box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.15);
    }
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 300px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

.glass-table {
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
}

.table-head {
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 120px 100px 80px 80px;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.head-cell {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cell-status,
.cell-actions {
  text-align: center;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 20px;
  color: rgba(200, 218, 255, 0.4);

  .v-icon {
    opacity: 0.5;
  }

  span {
    font-size: 0.9rem;
  }
}

.table-row {
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 120px 100px 80px 80px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
}

.row-cell {
  display: flex;
  align-items: center;
}

.cell-status,
.cell-actions {
  justify-content: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;

  &.person {
    background: rgba(33, 150, 243, 0.15);
    border: 1px solid rgba(33, 150, 243, 0.25);

    .v-icon {
      color: #2196F3;
    }
  }

  &.organization {
    background: rgba(255, 152, 0, 0.15);
    border: 1px solid rgba(255, 152, 0, 0.25);

    .v-icon {
      color: #FF9800;
    }
  }
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.user-contact {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.cell-email {
  display: flex;
  align-items: center;
  gap: 6px;
}

.email-text {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.8);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.verified-icon {
  color: #4CAF50;
  flex-shrink: 0;
}

.type-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;

  &.person {
    background: rgba(33, 150, 243, 0.1);
    color: #2196F3;
  }

  &.organization {
    background: rgba(255, 152, 0, 0.1);
    color: #FF9800;
  }
}

.status-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  &.status-active {
    background: rgba(76, 175, 80, 0.15);
    border-color: rgba(76, 175, 80, 0.25);

    .v-icon {
      color: #4CAF50;
    }
  }

  &.status-inactive {
    background: rgba(244, 67, 54, 0.1);
    border-color: rgba(244, 67, 54, 0.2);

    .v-icon {
      color: #F44336;
    }
  }

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
}

.edit-btn,
.sync-stripe-btn,
.impersonate-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.4);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }
}

.sync-stripe-btn:hover {
  background: rgba(103, 58, 183, 0.1);
  border-color: rgba(103, 58, 183, 0.25);
  color: #7C4DFF;
}

.impersonate-btn:hover {
  background: rgba(255, 152, 0, 0.1);
  border-color: rgba(255, 152, 0, 0.25);
  color: #FFA726;
}

.cell-actions {
  gap: 4px;
}

/* Sync Stripe Dialog */
.sync-stripe-dialog {
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;
  padding: 24px;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  .dialog-icon {
    color: #7C4DFF;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
  }
}

.dialog-content {
  margin-bottom: 24px;
}

.dialog-message {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.7);
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.user-info-box {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
  padding: 12px 16px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.info-value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.dialog-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.cancel-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.7);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
}

.confirm-btn {
  background: rgba(103, 58, 183, 0.2);
  border: 1px solid rgba(103, 58, 183, 0.3);
  color: #7C4DFF;

  &:hover:not(:disabled) {
    background: rgba(103, 58, 183, 0.3);
  }

  &.impersonate {
    background: rgba(255, 152, 0, 0.2);
    border-color: rgba(255, 152, 0, 0.3);
    color: #FFA726;

    &:hover:not(:disabled) {
      background: rgba(255, 152, 0, 0.3);
    }
  }
}

/* Impersonate Dialog */
.impersonate-dialog {
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 152, 0, 0.2);
  border-radius: 16px;
  padding: 24px;
}

.dialog-icon.impersonate {
  color: #FFA726;
}

.dialog-warning {
  font-size: 0.85rem;
  color: rgba(255, 152, 0, 0.9);
  margin: 16px 0 0 0;
  padding: 10px 12px;
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.2);
  border-radius: 8px;
  line-height: 1.4;
}

.info-row + .info-row {
  margin-top: 6px;
}

@media (max-width: 1024px) {
  .table-head,
  .table-row {
    grid-template-columns: 1fr 1fr 100px 80px 60px 70px;
  }
}

@media (max-width: 768px) {
  .table-head,
  .table-row {
    grid-template-columns: 1fr 80px 60px 70px;
  }

  .cell-email,
  .cell-type {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .table-row,
  .status-toggle,
  .edit-btn,
  .stat-pill {
    transition: none;
  }

  .status-toggle:hover,
  .stat-pill:hover {
    transform: none;
  }
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 80px;
  text-align: center;
}
</style>
